import pick from 'lodash/pick';
import ObjectUtils from '@spothero/utils/object';
import {
    buildImageTemplate,
    buildUniqueRateAmenities,
    formatDistance,
    formatHoursOfOperation,
} from '../../utils';
import {
    OBJECT_TRANSIENT_FACILITY_SUMMARY,
    OBJECT_TRANSIENT_FACILITY,
} from '@/proptypes/index';
import {formatTransientRate} from './formatTransientRate';
/**
 * Formats data for a single transient facility
 *
 * @function formatTransientFacility
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientFacilityParking|Documentation}
 * @param {object} data - The untransformed data from the API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @param {object} formatOptions.userRequestedStarts - The start date/time that the request was made with
 * @param {object} formatOptions.userRequestedEnds - The end date/time that the request was made with
 * @param {string} formatOptions.eventId - The unique monolith ID corresponding to an event
 * @param {string} formatOptions.timezone - The timezone of the nearest city, used for formatting date/time
 * @param {string} formatOptions.rebookId - The ID of the original reservation that we are rebooking for
 * @param {boolean} formatOptions.isFacilitySummary - If true, will return the shortened version of the facility data (e.g. to display on spot cards)
 * @returns {object} The formatted transient facility data.
 */

export function formatTransientFacility(data, formatOptions = {}) {
    const {
        userRequestedStarts,
        userRequestedEnds,
        eventId,
        timezone,
        rebookId,
        isFacilitySummary,
    } = formatOptions;
    const dataFormat = isFacilitySummary
        ? OBJECT_TRANSIENT_FACILITY_SUMMARY
        : OBJECT_TRANSIENT_FACILITY;
    const facilityData = ObjectUtils.camelizeKeys(data);

    const {
        availability,
        distance,
        facility: {common, transient},
        rates: ratesResponse,
        vehicle,
    } = facilityData;

    const {id: spotId, slug: facilitySlug, hoursOfOperation} = common;
    // only keep properties that have corresponding transient rate prop types
    const rates = ratesResponse.map(
        ({quote, transient: additionalRateDetails}) =>
            formatTransientRate({
                spotId,
                facilitySlug,
                quote,
                additionalRateDetails,
                userRequestedStarts,
                userRequestedEnds,
                eventId,
                timezone,
                rebookId,
            })
    );

    // Opted to Alias for ease of testing
    const prePick = {
        isV2: true,
        ...common,
        ...transient,
        images: common.images.map(buildImageTemplate),
        spotId, // spotId is relied on in a lot of places over id
        availability,
        distance: formatDistance(distance),
        rates,
        selectedRate: rates[0],
        allAmenities: buildUniqueRateAmenities(rates),
        hoursOfOperation: formatHoursOfOperation(hoursOfOperation),
        hasMultipleRates: rates.length > 1,
        visualFlags: common.visualFlags,
        vehicle,
    };

    // only keep properties that have corresponding transient facility prop types
    return pick(prePick, Object.keys(dataFormat));
}
